import { useMemo } from "react"
import { useCustomerData } from "context/customer-provider"
import type { AvailableCustomer } from "types/customer"

export type MySettings = {
    vehicle: string | null
    officerCode: string | null
    // "row" = the account's membership row for the active customer decided (a
    // null there is "none"); "home_row" = RR staff without a row here carry
    // their default customer's values; "none" = nothing applies (no entry, the
    // degraded fallback list); "pending" = /me/customers has not answered yet —
    // nothing is known, and nothing should be decided on it (no redirect, no
    // URL filter)
    source: "row" | "home_row" | "none" | "pending"
    // false while /me/customers is in flight; consumers that act once (URL
    // filters, redirects, one-shot preselects) must wait for true
    settled: boolean
}

const fromEntry = (entry: AvailableCustomer, source: "row" | "home_row"): MySettings => ({
    vehicle: entry.vehicle ?? null,
    officerCode: entry.confirm_officer_code ?? null,
    source,
    settled: true,
})

// The rule, kept pure so it can be table-tested and stays identical to
// rr-api's resolveUserSettings (api/utils/membership.js): a row-backed entry
// ("home" / "membership") is authoritative even when its values are null; a
// super-admin entry (RR staff, no row at this customer) carries the staff
// member's default-customer entry; anything else knows nothing. Nothing is
// known until the list settled.
export const resolveMySettings = (customerId: string, customers: AvailableCustomer[], settled: boolean): MySettings => {
    if (!settled) return { vehicle: null, officerCode: null, source: "pending", settled: false }
    const entry = customers.find((customer) => customer.customer_id === customerId)
    if (entry && (entry.source === "home" || entry.source === "membership")) return fromEntry(entry, "row")
    if (entry?.source === "super_admin") {
        const home = customers.find((customer) => customer.source === "home")
        if (home) return fromEntry(home, "home_row")
    }
    return { vehicle: null, officerCode: null, source: "none", settled: true }
}

// ART-3182: the signed-in account's vehicle and asset-system officer code AT
// THE ACTIVE CUSTOMER, from /me/customers (config.user_customer). They used to
// be global Auth0 profile fields; nothing reads those any more. The hook
// reports nothing until the list settled, so surfaces that act once
// (Detections URL filter, RV2 preselect, the mobile /my-device redirect)
// never act on a guess.
const useMySettings = (): MySettings => {
    const { customerId, availableCustomers, availableCustomersSettled } = useCustomerData()
    return useMemo(
        () => resolveMySettings(customerId, availableCustomers, availableCustomersSettled),
        [availableCustomers, availableCustomersSettled, customerId],
    )
}

export default useMySettings
